import React, { useState, useRef, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./DatePicker.css";
import calendarIcon from "../../../../assets/Main/icon_calendar.png";

export default function CustomDatePicker({
  value,
  onChange,
  placeholder = "ДД.ММ.ГГГГ",
  error,
  disabled
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Определяем мобильное устройство
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Закрытие календаря при клике вне
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Функция для преобразования строки ДД.ММ.ГГГГ в объект Date
  const parseDateString = (dateString) => {
    if (!dateString || dateString.length !== 10) return null;
    
    const parts = dateString.split('.');
    if (parts.length !== 3) return null;
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // месяцы в JS от 0 до 11
    const year = parseInt(parts[2], 10);
    
    // Проверяем, что получились валидные числа
    if (isNaN(day) || isNaN(month) || isNaN(year) || year < 1900 || year > 2100) return null;
    
    const date = new Date(year, month, day);
    
    // Проверяем, что дата валидная
    if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
      return null;
    }
    
    return date;
  };

  // Форматирование даты из календаря (ДД.ММ.ГГГГ)
  const handleSelect = (date) => {
    if (!date) return;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const formatted = `${day}.${month}.${year}`;
    onChange(formatted);
    setOpen(false);
  };

  // Маска ввода вручную (ДД.ММ.ГГГГ) - с 4 цифрами года
  const handleInputChange = (e) => {
    let v = e.target.value.replace(/\D/g, "").slice(0, 8); // максимум 8 цифр (ДДММГГГГ)
    
    // Форматируем с точками
    if (v.length > 4) {
      // ДД.ММ.ГГГГ (4 цифры года)
      v = v.slice(0, 2) + "." + v.slice(2, 4) + "." + v.slice(4, 8);
    } else if (v.length > 2) {
      v = v.slice(0, 2) + "." + v.slice(2);
    }

    onChange(v);
  };

  // Форматирование заголовка месяца (на русском)
  const formatNavigationLabel = ({ date, view }) => {
    if (view === "month") {
      const months = [
        "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
        "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
      ];
      return `${months[date.getMonth()]} ${date.getFullYear()}`;
    }
    return "";
  };

  // Преобразуем строку value в объект Date для Calendar
  const calendarValue = parseDateString(value);

  return (
    <div
      className="date-picker-wrapper"
      ref={wrapperRef}
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      {/* Поле ввода */}
      <input
        ref={inputRef}
        type="tel"
        className={`date-input ${error ? "date-input--error" : ""}`}
        value={value || ""}
        onChange={handleInputChange}
        placeholder={placeholder}
        maxLength={10}
        disabled={disabled}
        inputMode="numeric"  
        pattern="[0-9]*"
        style={{
          width: "100%",
          height: isMobile ? "48px" : "55px",
          padding: isMobile ? "0 44px 0 16px" : "0 50px 0 16px",
          fontSize: isMobile ? "14px" : "18px",
          borderRadius: "7px",
        }}
      />

      {/* Иконка календаря */}
      <img
        src={calendarIcon}
        alt="Календарь"
        className="calendar-icon"
        onClick={() => !disabled && setOpen(!open)}
        style={{
          width: isMobile ? "20px" : "22px",
          height: isMobile ? "20px" : "22px",
          right: isMobile ? "12px" : "16px",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
      />

      {/* Календарь-попап */}
      {open && !disabled && (
        <div
          className="calendar-popup"
          style={{
            position: "absolute",
            zIndex: 1000,
            background: "#fff",
            borderRadius: isMobile ? "12px" : "14px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            bottom: "100%",
            right: isMobile ? "0" : "0",
            left: isMobile ? "0" : "auto",
            width: isMobile ? "100%" : "340px",
            maxWidth: "100%",
            padding: isMobile ? "12px" : "16px",
            marginTop: isMobile ? "8px" : "0",
            marginBottom: isMobile ? "8px" : "12px",
          }}
        >
          <Calendar
            onClickDay={handleSelect}
            className="custom-calendar"
            value={calendarValue}
            maxDetail="month"
            minDetail="month"
            navigationLabel={formatNavigationLabel}
            prevLabel="‹"
            nextLabel="›"
            showNeighboringMonth={false}
            tileClassName={({ date, view }) =>
              view === "month" && date.getDay() === 0 ? "react-calendar__tile--weekend" : null
            }
            // Делаем календарь компактнее на мобильных
            style={{
              width: "100%",
              fontSize: isMobile ? "14px" : "16px",
            }}
          />
        </div>
      )}
    </div>
  );
}